import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LogoUpload } from '@/components/LogoUpload';
import { Check, X, Edit3 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function LogosView({
  currentEvent,
  handleEventLogoUpload,
  handleTeamLogoUpload,
  renamingTeamId,
  setRenamingTeamId,
  teamRenameValue,
  setTeamRenameValue,
  handleTeamRename,
}) {
  return (
    <div className="space-y-6">
      {/* Event Logo */}
      <Card className="border-slate-200 shadow-card" data-testid="event-logo-section">
        <CardHeader>
          <CardTitle className="text-base font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
            TOURNAMENT LOGO
          </CardTitle>
          <p className="text-xs text-slate-500">This logo appears in your PDF exports and shared schedule</p>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <LogoUpload
              type="event"
              currentLogo={currentEvent.event_logo}
              onUploadComplete={handleEventLogoUpload}
              size="lg"
            />
            <div className="text-sm text-slate-500">
              <p className="font-medium text-slate-700">Upload your tournament or league logo</p>
              <p>Accepted formats: JPEG, PNG, GIF, WebP (max 2MB)</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Team Logos */}
      <Card className="border-slate-200 shadow-card" data-testid="team-logos-section">
        <CardHeader>
          <CardTitle className="text-base font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
            TEAM LOGOS
          </CardTitle>
          <p className="text-xs text-slate-500">Add logos for each team. These appear in schedule tables and exports.</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {currentEvent.teams?.map((team) => {
              const teamLogo = currentEvent.team_logos?.[team.id];
              const isRenaming = renamingTeamId === team.id;
              return (
                <div key={team.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg group" data-testid={`team-logo-row-${team.id}`}>
                  <LogoUpload
                    type="team"
                    currentLogo={teamLogo}
                    onUploadComplete={(path) => handleTeamLogoUpload(team.id, path)}
                    size="md"
                  />
                  <div className="flex-1 min-w-0">
                    {isRenaming ? (
                      <div className="flex items-center gap-1">
                        <Input
                          value={teamRenameValue}
                          onChange={(e) => setTeamRenameValue(e.target.value)}
                          className="h-7 text-sm"
                          autoFocus
                          data-testid={`team-rename-input-${team.id}`}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleTeamRename(team.id);
                            if (e.key === 'Escape') { setRenamingTeamId(null); setTeamRenameValue(''); }
                          }}
                        />
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-green-600" onClick={() => handleTeamRename(team.id)} data-testid={`team-rename-confirm-${team.id}`}>
                          <Check className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-slate-400" onClick={() => { setRenamingTeamId(null); setTeamRenameValue(''); }} data-testid={`team-rename-cancel-${team.id}`}>
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <p className="font-semibold text-sm text-slate-800 truncate">{team.name}</p>
                        <button
                          onClick={() => { setRenamingTeamId(team.id); setTeamRenameValue(team.name); }}
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-[#00247D]"
                          data-testid={`team-rename-btn-${team.id}`}
                          title="Rename team"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                    <p className="text-xs text-slate-400">{teamLogo ? 'Logo uploaded' : 'Click to add logo'}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
